<template>
  <div v-if="!isLoading && !returnDetails.FailureCode" class="container return-details-container">
    <div 
      class="btn btn-success noprint"
      @click="print"
    >
      <div>
        Print Return
      </div>
    </div>
    <b-row class="row no-gutters">
      <b-col md="6">
        <h1>Return # {{ returnId }}</h1>
      </b-col>
      <b-col md="6">
        <strong class="pull-right noprint">Status: {{ getContentItem(returnDetails.ReturnStatus) }} </strong>
      </b-col>
    </b-row>

    <b-row class="no-gutters">
      <b-col md="12">
        <div class="returnDetailSection">RETURN INFORMATION</div>
      </b-col>
    </b-row>

    <b-row class="return-info-row">
      <b-col md="4">
        <b-row class="rd__print-field-pair">
          <b-col md="6" class="rd__print-field-label">
            <strong>Dealer Return Reference: </strong>
          </b-col>
          <b-col md="6" class="rd__print-field-value">
            {{ returnDetails.CustomerReferenceNumber }}
          </b-col>
        </b-row>
      </b-col>
      <b-col md="4">
        <b-row class="rd__print-field-pair">
          <b-col md="6" class="rd__print-field-label">
            <strong>Credit Invoice Number: </strong>
          </b-col>
          <b-col md="6" class="rd__print-field-value">
            <div v-for="(credit, key) in returnDetails.Credits" :key="key">
              <router-link :to="GetCreditInvoiceLink(credit)">{{ credit.InvoiceNumber }}</router-link
              ><span v-if="key !== returnDetails.Credits.length - 1">,</span>
            </div>
          </b-col>
        </b-row>
      </b-col>
      <b-col md="4">
        <b-row class="rd__print-field-pair">
          <b-col md="6" class="rd__print-field-label">
            <strong>Contact Name: </strong>
          </b-col>
          <b-col md="6" class="rd__print-field-value">
            {{ returnDetails.ContactName }}
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row class="return-detail-section-row return-info-row">
      <b-col md="4">
        <b-row class="rd__print-field-pair">
          <b-col md="6" class="rd__print-field-label">
            <strong>Return Number: </strong>
          </b-col>
          <b-col md="6" class="rd__print-field-value">
            {{ returnDetails.ReturnAuthorizationCode }}
          </b-col>
        </b-row>
      </b-col>
      <b-col md="4">
        <b-row class="rd__print-field-pair">
          <b-col md="6" class="rd__print-field-label">
            <strong>Date Submitted: </strong>
          </b-col>
          <b-col md="6" class="rd__print-field-value">
            {{ returnDetails.DateSubmitted }}
          </b-col>
        </b-row>
      </b-col>
      <b-col md="4">
        <b-row class="rd__print-field-pair">
          <b-col md="6" class="rd__print-field-label">
            <strong>Contact Email: </strong>
          </b-col>
          <b-col md="6" class="rd__print-field-value">
            {{ returnDetails.ContactEmailAddress }}
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row class="return-detail-section-row return-info-row">
      <b-col md="4">
        <b-row class="rd__print-field-pair">
          <b-col md="6" class="rd__print-field-label">
            <strong>Return Method: </strong>
          </b-col>
          <b-col md="6" class="rd__print-field-value">
            {{ getContentItem(returnDetails.Disposition) }}
          </b-col>
        </b-row>
      </b-col>
      <b-col md="4">
        <b-row class="rd__print-field-pair">
          <b-col md="6" class="rd__print-field-label">
            <strong>Date Credited: </strong>
          </b-col>
          <b-col md="6" class="rd__print-field-value">
            {{ returnDetails.DateCredited }}
          </b-col>
        </b-row>
      </b-col>
      <b-col md="4">
        <b-row class="rd__print-field-pair">
          <b-col md="6" class="rd__print-field-label">
            <strong>Contact Number: </strong>
          </b-col>
          <b-col md="6" class="rd__print-field-value">
            {{ returnDetails.ContactPhone }}
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row class="return-detail-section-row return-info-row">
      <b-col md="4">
        <b-row class="rd__print-field-pair">
          <b-col class="rd__print-field-label"><strong>Return Type: </strong></b-col>
          <b-col v-if="returnDetails.BaseReturnType == 'Unknown'" class="rd__print-field-value">
            {{ getContentItem("NoReturnTypeSpecified") }}
          </b-col>
          <b-col v-else-if="returnDetails.BaseReturnType == 'Surplus'" class="rd__print-field-value">
            {{ getContentItem("SurplusReturn") }}
          </b-col>
          <b-col v-else-if="returnDetails.BaseReturnType == 'Express'" class="rd__print-field-value">
            {{ getContentItem("ExpressReturn") }}
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row class="returnDetailSection marginTop no-gutters">
      <b-col> PARTS INFORMATION </b-col>
    </b-row>

    <b-row>
      <b-col>
        <DnTable
          hover
          striped
          :fields="returnItemsColumns"
          :items="returnDetails.Items"
          stacked="md"
          :no-provider-filtering="true"
          show-empty
          :no-local-sorting="true"
          class="blue"
        >
          <template #partNumber="{ row }">
            <part-details-link :oem-part-code="row.item.OemPartCode" />
          </template>
          <template #Price="{ row }">
            <currency-text :amount="GetItemPrice(row.item)" :currency-code="returnDetails.Currency" />
          </template>
          <template #extendedPrice="{ row }">
            <currency-text
              :amount="GetItemPrice(row.item) * row.item.Quantity"
              :currency-code="returnDetails.Currency"
            />
          </template>
        </DnTable>
      </b-col>
    </b-row>

    <b-row class="no-gutters">
      <b-col class="tableSummary">
        <b-row class="topRow">
          <b-col>Total Part Value:</b-col>
          <b-col class="text-right">
            <currency-text :amount="returnDetails.TotalValue" :currency-code="returnDetails.Currency" />
          </b-col>
        </b-row>
        <b-row
          v-if="returnDetails.CoreItems !== undefined && returnDetails.CoreItems.length > 0"
          id="corePrice"
          class="rowTopBorder no-gutters expand-sub-fees"
        >
          <b-col>
            <a class="textSmall noHrefLink" @click="ToggleShowDetails('core', $event)"
              >Details <i class="icon-chevron-right"
            /></a>
            Core Costs:
            <ul v-if="showDetails.core" class="sub-fees">
              <li v-for="(coreItem, key) in returnDetails.CoreItems" :key="key">
                {{ coreItem.OemPartCode }}:
                <currency-text :amount="coreItem.CorePrice" :currency-code="returnDetails.Currency" />
              </li>
            </ul>
          </b-col>
          <b-col class="text-right">
            <currency-text :amount="returnDetails.CoreTotal" :currency-code="returnDetails.Currency" />
          </b-col>
        </b-row>
        <b-row
          v-if="returnDetails.InsuranceItems !== undefined && returnDetails.InsuranceItems.length > 0"
          id="insurance"
          class="rowTopBorder no-gutters expand-sub-fees"
        >
          <b-col>
            <a class="textSmall noHrefLink" @click="ToggleShowDetails('insurance', $event)"
              >Details <i class="icon-chevron-right"
            /></a>
            Insurance Charges:
            <ul v-if="showDetails.insurance" class="sub-fees">
              <li v-for="(insuranceItem, key) in returnDetails.InsuranceItems" :key="key">
                {{ insuranceItem.OemPartCode }}:
                <currency-text :amount="insuranceItem.Insurance" :currency-code="returnDetails.Currency" />
              </li>
            </ul>
          </b-col>
          <b-col class="text-right">
            <currency-text :amount="returnDetails.InsuranceItemsTotal" :currency-code="returnDetails.Currency" />
          </b-col>
        </b-row>
        <b-row
          v-if="returnDetails.OtherFeeItems !== undefined && returnDetails.OtherFeeItems.length > 0"
          id="otherFees"
          class="rowTopBorder no-gutters expand-sub-fees"
        >
          <b-col>
            <a class="textSmall noHrefLink" @click="ToggleShowDetails('otherFees')"
              >Details <i class="icon-chevron-right"
            /></a>
            Other Fees:
            <ul v-if="showDetails.otherFees" class="sub-fees">
              <li v-for="(otherFeeItem, key) in returnDetails.OtherFeeItems" :key="key">
                {{ otherFeeItem.OemPartCode }}:
                <currency-text :amount="otherFeeItem.OtherFees" :currency-code="returnDetails.Currency" />
              </li>
            </ul>
          </b-col>
          <b-col class="text-right">
            <currency-text :amount="returnDetails.OtherFeeItemsTotal" :currency-code="returnDetails.Currency" />
          </b-col>
        </b-row>
        <b-row id="returnFee" class="no-gutters rowTopBorder expand-sub-fees" visible="False">
          <b-col>
            <a class="textSmall noHrefLink" @click="ToggleShowDetails('returnFees')"
              >Details <i class="icon-chevron-right"
            /></a>
            Total Return Fees:
            <ul v-if="showDetails.returnFees" class="sub-fees">
              <li v-for="(feeItem, key) in returnDetails.ReturnFeeItems" :key="key">
                {{ feeItem.OemPartCode }}:
                <currency-text :amount="feeItem.ReturnFee" :currency-code="returnDetails.Currency" />
              </li>
            </ul>
          </b-col>
          <b-col class="text-right">
            <currency-text :amount="returnDetails.ReturnFeeTotal" :currency-code="returnDetails.Currency" />
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-row class="no-gutters summary">
      <b-col>TOTAL AMOUNT CREDITED:</b-col>
      <b-col class="text-right">
        <currency-text :amount="returnDetails.TotalAmountCredited" :currency-code="returnDetails.Currency" />
      </b-col>
    </b-row>
  </div>
  <div v-else-if="!isLoading" class="container">
    <b-row class="row no-gutters return-details-error-wrapper">
      <b-col>
        <div>Return # {{ returnId }}</div>
        <br />
        <div>
          {{ returnDetails.FailureMessage }}: ({{ returnDetails.FailureCode }}:
          {{ returnDetails.InformationalMessage }})
        </div>
      </b-col>
    </b-row>
  </div>
  <div v-else class="d-flex align-items-center justify-content-center">
    <loading-spinner />
  </div>
</template>

<script>
import CurrencyText from "@/components/Elements/CurrencyText";
import PartDetailsLink from "@/components/Elements/PartDetailsLink";
import LoadingSpinner from "@/components/Elements/LoadingSpinner";
import { ReturnsService } from "@/services/ReturnsService";
import { mapGetters } from "vuex";
import DnTable from "@/components/Elements/DnTable/DnTable.vue";

export default {
  props: {
    returnId: String,
    salesOrg: String,
    accountId: String,
    returnSource: String,
  },

  data: () => ({
    returnItemsColumns: [
      { key: "partNumber", label: "Part Number", sortable: false },
      { key: "PartDescription", label: "Description", sortable: false },
      { key: "Quantity", label: "Quantity", sortable: false },
      { key: "Price", lablel: "Unit Price", sortable: false },
      { key: "extendedPrice", label: "Ext. Price", sortable: false },
    ],
    returnDetails: {
      ReturnStatus: "Status",
    },
    showDetails: {
      core: false,
      freight: false,
      insurance: false,
      otherFees: false,
      returnFees: false,
    },
    isLoading: true,
  }),

  mounted() {
    this.isLoading = true;
    ReturnsService.GetReturnDetails(
      this.returnId,
      this.salesOrg,
      this.returnSource,
      this.accountId,
      this.user.UserId
    ).then((result) => {
      this.returnDetails = result.data;
      this.isLoading = false;
    });
  },

  methods: {
    ToggleShowDetails(key, event) {
      this.showDetails[key] = !this.showDetails[key];
    },
    print() {
      window.print();
    },
    GetCreditInvoiceLink(credit) {
      if (this.IsNullOrUndefined(credit, "Id")) {
        return "";
      }
      return "/orders/credits/detail?id=" + credit.Id + "&source=" + credit.InvoiceSource;
    },
    GetItemPrice(item) {
      if (item.NetPrice > 0) {
        if (item.Quantity > 0) {
          return item.NetPrice / item.Quantity;
        }
        return item.NetPrice;
      }
      return item.Price;
    },
  },

  computed: {
    ...mapGetters(["user", "getContentItem"]),
  },

  components: {
    CurrencyText,
    PartDetailsLink,
    LoadingSpinner,
    DnTable,
  },
};
</script>

<style lang="scss">
.returnDetailSection {
  font-size: 18px;
  background-color: #e4e9ec;
  padding: 13px;
  margin-bottom: 25px;
  font-weight: bold;
}

.marginTop {
  margin-top: 35px;
}

.tableSummary {
  border: 1px solid black;
  font-weight: bold;
  font-size: 15px;
  margin-top: 30px;
}

.topRow {
  padding: 8px;
}

.rowTopBorder {
  border-top: 1px solid #ddd;
  padding: 8px;
}

.textSmall {
  font-size: 10px;
}

.summary {
  background-color: #205cb7;
  color: #fff;
  padding: 8px;
  font-weight: bold;
  font-size: 15px;
}
.return-details-error-wrapper {
  padding: 12px;
  border: 1px solid $red;
  background-color: $light-red;
}

@media print {
  .return-details-container {
    .return-info-row {
      display: flex !important;
      flex-wrap: wrap;
      width: 100% !important;
      margin-bottom: 16px;
      page-break-inside: avoid;
    }

    .return-info-row > .col-md-4 {
      flex: 0 0 33.33% !important;
      max-width: 33.33% !important;
      width: 33.33% !important;
      padding-right: 16px;
      box-sizing: border-box;
    }

    .rd__print-field-pair {
      display: flex !important;
      flex-direction: column !important;
      width: 100% !important;
      margin-bottom: 10px;
    }

    .rd__print-field-label {
      max-width: 100% !important;
      width: 100% !important;
      white-space: normal;
    }

    .rd__print-field-value {
      max-width: 100% !important;
      width: 100% !important;
      word-break: break-word;
    }

    .returnDetailSection {
      margin-top: 20px;
      margin-bottom: 16px;
    }
  }
}
</style>